import React from 'react';
import { useLocations } from '@/domain/events/useEvents';
import type { StatusTone } from '@/domain/warehouse';
import type { TransferRecord, TransferRecordWaypoint, TransferStopProgress } from '@/data/transferRecord';
import JobAddressLink from '../../../ui/JobAddressLink';
import TransferActionButton from './TransferActionButton';
import {
  formatSiteDateTime,
  formatSiteDeadline,
  formatSiteMoment,
  shipmentDetail,
  isR2Record,
  shipmentTitle,
  siteName,
  stopKindLabel,
  stopNextSteps,
  stopStatus,
  trackingUrl,
  type TransferActionViews,
} from './transferRecordView';

type TransferRouteSectionProps = {
  record: TransferRecord;
  actions: TransferActionViews;
  busy: boolean;
  onAddStop: () => void;
  onEditStop: (stopId: string) => void;
  onStopProgress: (stopId: string, status: TransferStopProgress) => void;
  onAddShipment: () => void;
  onEditShipment: (shipmentId: string) => void;
};

const ON_THE_ROAD = new Set(['partially_shipped', 'shipped', 'in_transit', 'partially_received']);
const PICKING = new Set(['partially_picked', 'picked']);

function Chip({ tone, children }: { tone: StatusTone; children: React.ReactNode }) {
  return <span className="status-chip" data-tone={tone} data-size="sm">{children}</span>;
}

function RouteStop({
  name,
  detail,
  notes,
  chip,
  tone,
  marker,
  actions,
}: {
  name: React.ReactNode;
  detail: string | null;
  notes?: string | null;
  chip: { label: string; tone: StatusTone };
  tone: 'done' | 'current' | 'upcoming';
  marker: 'site' | 'stop' | 'road';
  actions?: React.ReactNode;
}) {
  return (
    <li className="transfer-record-stops__stop" data-state={tone} data-marker={marker}>
      <span className="transfer-record-stops__dot" aria-hidden="true" />
      <div className="transfer-record-stops__body">
        <strong className="transfer-record-stops__name">{name}</strong>
        {detail ? <span className="transfer-record-stops__detail">{detail}</span> : null}
        {notes ? <span className="transfer-record-stops__notes">{notes}</span> : null}
        {actions ? <span className="transfer-record-stops__actions">{actions}</span> : null}
      </div>
      <Chip tone={chip.tone}>{chip.label}</Chip>
    </li>
  );
}

/** Route & shipments: the stops in order (origin, stops, the road, destination) and the shipments. */
export default function TransferRouteSection({
  record,
  actions,
  busy,
  onAddStop,
  onEditStop,
  onStopProgress,
  onAddShipment,
  onEditShipment,
}: TransferRouteSectionProps) {
  const { transfer, waypoints, shipments } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const locationsQuery = useLocations({ allowedLocationIds: null, enabled: waypoints.length > 0 });
  const zoneOf = React.useCallback((stop: TransferRecordWaypoint) => {
    const id = stop.address ? null : stop.location?.id;
    return (id ? locationsQuery.data?.find((location) => location.location_id === id)?.timezone : null) ?? toZone;
  }, [locationsQuery.data, toZone]);

  const shipped = Boolean(transfer.actualShipAt) || ON_THE_ROAD.has(transfer.status) || ['received', 'complete'].includes(transfer.status);
  const received = ['received', 'complete'].includes(transfer.status);
  const latestShipment = shipments[shipments.length - 1] ?? null;
  const canEditStops = actions.add_stop.allowed;
  const r2 = isR2Record(record);

  const origin = {
    name: siteName(transfer.fromSite, 'Shipping site'),
    detail: shipped
      ? ['Origin', transfer.actualShipAt ? `left ${formatSiteDateTime(transfer.actualShipAt, fromZone)}` : 'left'].join(' · ')
      : ['Origin', transfer.expectedShipAt ? `ships ${formatSiteMoment(transfer.expectedShipAt, fromZone)}` : null].filter(Boolean).join(' · '),
    chip: shipped
      ? { label: 'Left', tone: 'ok' as const }
      : PICKING.has(transfer.status) ? { label: 'Picking', tone: 'progress' as const } : { label: 'Not left', tone: 'neutral' as const },
  };
  const receivedAt = formatSiteDateTime(transfer.actualReceiveAt, toZone);
  const expectedBy = formatSiteDeadline(transfer.expectedReceiveAt, toZone);
  const destinationDetail = received
    ? receivedAt ? `Destination · received ${receivedAt}` : 'Destination · received'
    : expectedBy ? `Destination · expected ${expectedBy}` : 'Destination';
  const destinationChip = received
    ? { label: 'Received', tone: 'ok' as const }
    : transfer.status === 'partially_received'
      ? { label: 'Partly received', tone: 'warn' as const }
      : { label: 'Expected', tone: 'neutral' as const };

  return (
    <div className="transfer-record-panel">
      <div className="transfer-record-panel__head">
        <div className="transfer-record-panel__heading">
          <h2 className="transfer-record-panel__title">Route</h2>
          <p className="transfer-record-panel__intro">Stops in order. Each stop records arrive and leave times.</p>
        </div>
        <div className="transfer-record-panel__actions">
          <TransferActionButton className="btn sm" allowed={actions.add_stop.allowed} reason={actions.add_stop.reason} busy={busy} onClick={onAddStop}>
            Add stop
          </TransferActionButton>
          <TransferActionButton className="btn sm" allowed={actions.add_shipment.allowed} reason={actions.add_shipment.reason} busy={busy} onClick={onAddShipment}>
            Add shipment
          </TransferActionButton>
        </div>
      </div>

      <ol className="transfer-record-stops" aria-label="Route stops">
        <RouteStop name={origin.name} detail={origin.detail} chip={origin.chip} tone={shipped ? 'done' : 'current'} marker="site" />
        {waypoints.map((stop, index) => {
          const zone = zoneOf(stop);
          const status = stopStatus(stop.status);
          const arrive = formatSiteDateTime(stop.arriveAt, zone);
          const depart = formatSiteDateTime(stop.departAt, zone);
          const detail = [
            `Stop ${index + 1}`,
            stopKindLabel(stop.kind),
            arrive ? `arrives ${arrive}` : null,
            depart ? `leaves ${depart}` : null,
          ].filter(Boolean).join(' · ');
          const label = stop.address || stop.location?.name || 'Stop';
          const next = canEditStops ? stopNextSteps(stop.status, { r2 }) : [];
          return (
            <RouteStop
              key={stop.id}
              name={stop.address
                ? <JobAddressLink address={stop.address} label={`stop ${index + 1}`} />
                : label}
              detail={detail}
              notes={stop.notes}
              chip={status}
              tone={stop.status === 'shipped' ? 'done' : stop.status === 'pending' ? 'upcoming' : 'current'}
              marker="stop"
              actions={(
                <>
                  <TransferActionButton
                    className="btn xs"
                    allowed={actions.add_stop.allowed}
                    reason={actions.add_stop.reason}
                    busy={busy}
                    ariaLabel={`Edit stop ${index + 1}`}
                    onClick={() => onEditStop(stop.id)}
                  >
                    Edit
                  </TransferActionButton>
                  {next.map((step) => (
                    <button
                      key={step.status}
                      type="button"
                      className="btn xs"
                      disabled={busy}
                      onClick={() => onStopProgress(stop.id, step.status)}
                    >
                      {step.label}
                    </button>
                  ))}
                </>
              )}
            />
          );
        })}
        {shipped && !received ? (
          <RouteStop
            name="On the road"
            detail={latestShipment
              ? [shipmentTitle(latestShipment), shipmentDetail(latestShipment, fromZone, toZone)].filter(Boolean).join(' · ')
              : 'No shipment details yet'}
            chip={{ label: 'In transit', tone: 'progress' }}
            tone="current"
            marker="road"
          />
        ) : null}
        <RouteStop
          name={siteName(transfer.toSite, 'Receiving site')}
          detail={destinationDetail}
          chip={destinationChip}
          tone={received ? 'done' : 'upcoming'}
          marker="site"
        />
      </ol>

      <h3 className="transfer-record-panel__subhead">Shipments</h3>
      {shipments.length ? (
        <ul className="transfer-record-shipments" aria-label="Shipments">
          {shipments.map((shipment) => {
            const url = trackingUrl(shipment.carrier, shipment.trackingNo);
            const detail = shipmentDetail(shipment, fromZone, toZone);
            return (
              <li key={shipment.id} className="transfer-record-shipment">
                <div className="transfer-record-shipment__main">
                  <strong>
                    {shipmentTitle(shipment)}
                    {shipment.trackingNo ? <> · <span className="transfer-record-mono">{shipment.trackingNo}</span></> : null}
                  </strong>
                  {detail ? <span>{detail}</span> : null}
                </div>
                <div className="transfer-record-shipment__actions">
                  {url ? (
                    <a
                      className="btn xs"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Track ${shipment.trackingNo ?? 'this shipment'} on the carrier's site (opens a new tab)`}
                    >
                      Track
                    </a>
                  ) : null}
                  <TransferActionButton
                    className="btn xs"
                    allowed={actions.add_shipment.allowed}
                    reason={actions.add_shipment.reason}
                    busy={busy}
                    ariaLabel={`Edit ${shipmentTitle(shipment)}`}
                    onClick={() => onEditShipment(shipment.id)}
                  >
                    Edit
                  </TransferActionButton>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="transfer-record-empty">No shipments yet. Add the carrier parcel or truck run when it is booked.</p>
      )}
      {transfer.trackingNo && !shipments.some((shipment) => shipment.trackingNo === transfer.trackingNo) ? (
        <p className="transfer-record-panel__footnote">
          Tracking number recorded on the transfer: <span className="transfer-record-mono">{transfer.trackingNo}</span>
        </p>
      ) : null}
    </div>
  );
}
